import React, { useMemo, useContext } from "react";
import { Context } from "../../context/context";
import { primary } from "../../constants/theme";
import { sendAnalytics } from "../../utils/analyticsCommon";

const PricingVehicleDetails = ({ vehicleData, productType, location = "", minPrice = "", maxPrice = "" }) => {
  const { showLoanPopup } = useContext(Context);

  const vehicleName = useMemo(
    () => vehicleData?.vehicle_name || vehicleData?.name || "",
    [vehicleData]
  );

  const description = useMemo(() => {
    const makeModel = vehicleData?.brand && vehicleName 
      ? `${vehicleData.brand} ${vehicleName}` 
      : vehicleName || "[Make Model]";
    const cityState = location || "[city, state]";
    const cityStateCapitalized = location || "[City, State]";
    const minPriceText = minPrice || "[Min Price]";
    const maxPriceText = maxPrice || "[Max Price]";

    return `${makeModel} On Road Price in ${cityState?.split(",")[0]}\n\nThe ${makeModel} price in ${cityStateCapitalized} starts from ₹ ${minPriceText} Lakh and goes up to ₹ ${maxPriceText} Lakh (Ex-showroom). Turno Club is the one-stop shop for finding the best deals on new or used three-wheelers. Whether you are looking for a passenger or commercial electric auto, you are at the right place.`;
  }, [vehicleData, vehicleName, location, minPrice, maxPrice]);

  const exShowroomPrice = useMemo(() => {
    if (minPrice && maxPrice) {
      return `₹${minPrice}L - ${maxPrice}L`;
    }
    if (minPrice) {
      return `₹${minPrice}L`;
    }
    return "₹3.14L - 3.58L"; // Fallback
  }, [minPrice, maxPrice]);

  if (!vehicleData) return null;

  const handleGetOnRoadPriceClick = () => {
    sendAnalytics(
      "pricing_page_get_on_road_price_clicked",
      {
        vehicle_id: vehicleData?.vehicle_id,
        vehicle_name: vehicleName,
        variant_type: productType?.type,
      },
      "en"
    );
    const loanPayload = {
      brand: vehicleData?.brand,
      formTrigger: "pricing_page_get_on_road_price",
      vehicle_id: vehicleData?.vehicle_id,
      vehicle_name: vehicleName,
      CTAText: "Get On Road Price",
      headingText: "Enter Your Mobile Number",
      showThankYouMessage: true,
    };
    showLoanPopup(loanPayload);
  };

  return (
    <div className="w-full px-3 md:px-0">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-3 md:p-4 flex flex-col">
        <h2 className="text-[20px] md:text-xl font-extrabold tracking-tight text-gray-900 mb-0">
          {description.split("\n")[0] || vehicleName || "On-Road Price"}
        </h2>

        <p className="mt-0 text-sm leading-relaxed text-gray-500 whitespace-pre-line">
          {description.split("\n").slice(1).join("\n").trim()}
        </p>

        <div className="mt-3 rounded-xl bg-sky-50 px-3.5 py-3 flex items-center justify-between">
          <p className="text-sm text-gray-900">
            Ex-Showroom Price
          </p>
          <p className="text-sm md:text-base font-bold text-sky-700">
            {exShowroomPrice}
          </p>
        </div>

        {/* Price breakup: keys visible, three rows */}
        <div className="mt-2.5 rounded-xl bg-white border border-gray-100 px-3.5 py-2">
          <div className="flex items-center justify-between py-1.5">
            <p className="text-[13px] text-gray-700">RTO</p>
            <p className="text-[13px] font-semibold text-gray-900 blur-sm select-none">
              ₹xx,xxx
            </p>
          </div>
          <div className="h-px bg-gray-100" />
          <div className="flex items-center justify-between py-1.5">
            <p className="text-[13px] text-gray-700">Insurance</p>
            <p className="text-[13px] font-semibold text-gray-900 blur-sm select-none">
              ₹xx,xxx
            </p>
          </div>
          <div className="h-px bg-gray-100" />
          <div className="flex items-center justify-between py-1.5">
            <p className="text-[13px] text-gray-700">Total</p>
            <p className="text-[13px] font-semibold text-gray-900 blur-sm select-none">
              ₹xx,xxx
            </p>
          </div>
        </div>

        <div className="mt-3">
          <button
            type="button"
            onClick={handleGetOnRoadPriceClick}
            className="w-full text-white font-semibold transition-colors hover:opacity-95"
            style={{
              background: primary?.main,
              borderRadius: "4px",
              padding: "12px 8px",
              fontSize: "16px",
            }}
          >
            Get On-Road Price
          </button>
        </div>

        <p className="mt-2.5 text-center text-xs" style={{ color: primary?.main }}>
          *Prices may vary by city
        </p>
      </div>
    </div>
  );
};

export default PricingVehicleDetails;

